import { useCreateCustomCdf } from 'features/dataLayers//customCdfs/useCreateCustomCdf'
import {
  CreateCdfParams,
  RawCdfEntry,
} from 'features/dataLayers/customCdfs/cdfGenerator'
import { useGetCustomCdfUpsertDeleteFns } from 'features/dataLayers/customCdfs/useGetCustomCdfUpsertDeleteFns'
import {
  callEmatCombinedCategoryWorker,
  useEmatCombinedCategoryData,
} from 'features/dataLayers/ematCategoricalHelpers'
import { LossByPercentageWorkerProps } from 'features/dataLayers/generation/coreLayers/wastage/calculateLossByPercentageData.worker'
import { FdeCdfSpecification } from 'features/dataLayers/generation/fdeCdfSpecification'
import { EmatCombinedCategoryWorkerProps } from 'features/dataLayers/generation/workers/calculateEmatCombinedCategoryData.worker'
import { LossWorkerProps } from 'features/dataLayers/generation/workers/calculateLossData.worker'
import { LossPerYearWorkerProps } from 'features/dataLayers/generation/workers/calculateLossPerYearData.worker'
import { RcaWorkerProps } from 'features/dataLayers/generation/workers/calculateRcaData.worker'
import { RemainingLifeWorkerProps } from 'features/dataLayers/generation/workers/calculateRemainingLifeData.worker'
import { ShipDeckPITThicknessWorkerProps } from 'features/dataLayers/generation/workers/calculateShipDeckPITThickness.worker'
import { ThicknessByMeanWorkerProps } from 'features/dataLayers/generation/workers/calculateThicknessByMeanData.worker'
import { TimeSinceLastRepairWorkerProps } from 'features/dataLayers/generation/workers/calculateTimeSinceLastRepairData.worker'
import { TminWorkerProps } from 'features/dataLayers/generation/workers/calculateTminData.worker'
import { getInspectionDate, useGetForecastComponentConfig } from 'features/fde'
import { RolodexCacheInstance } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import { useCallback, useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import {
  CustomCorrosionRegion,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoEntryBase,
  GeckoInspection,
  GeckoTMinMethod,
  isInspectionThicknessCDF,
  OverrideRegion,
  PredictedInspectionType,
} from 'types'
import { cantorPairing } from 'utils/cantorPairing'
import { DATE_FORMAT } from 'utils/fde/constants'

import { useGetGenerateDerivedCdfDataFn } from './useGetGenerateDerivedCdfDataFn'

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * A hook that generates the data for a derived CDF -- based on the `cdfSpecification` -- in a Web Worker.
 *
 * Note: all parameters (including the pieces of `overrides`) should be memoized, as this functions performs heavy calculations.
 */
export function useGenerateDerivedDataInWebWorker<RETURN = number>(
  cdfSpecification: FdeCdfSpecification,
  assetModel: GeckoAssetModel | undefined,
  baseThicknessCDF: GeckoComponentDataFrame | undefined,
  selectedInspection: GeckoInspection | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined,
  overrides: ParamOverrides = {},
  keepPreviousData: boolean = false
): {
  array: RETURN[][]
  isLoading: boolean
} {
  const componentId = baseThicknessCDF?.tags.component

  const { baseThicknessValuesAreLoading, calculateDataInWebWorkerFn } =
    useGetGenerateDerivedValuesInWebWorkerFn<RETURN>(
      cdfSpecification,
      componentId,
      assetModel,
      baseThicknessCDF,
      selectedInspection,
      forecastComponentConfig,
      overrides
    )

  // ------------------------------------------ Call Calculate Function ------------------------------------------

  const canCalculateData =
    cdfSpecification.canCreateCdfData(
      assetModel,
      componentId ?? '',
      forecastComponentConfig ?? {},
      selectedInspection
    ) && !isNil(calculateDataInWebWorkerFn)

  const assetModelJson = assetModel?.raw

  const { data, isLoading: rlCalcIsLoading } = useSWRImmutable(
    // key
    [
      cdfSpecification.source.id,
      cdfSpecification.view.id,
      cdfSpecification.tminMethod.id,
      baseThicknessCDF?.data.params,
      baseThicknessCDF?.generation,
      forecastComponentConfig,
      baseThicknessValuesAreLoading,
      overrides.appliedRegions,
      overrides.customCorrosionRegions,
      assetModelJson,
      selectedInspection?.generation,
    ],
    // function
    async () => (canCalculateData ? calculateDataInWebWorkerFn() : undefined),
    // options
    {
      // There is no cheap way to compare CDF data to know if it has changed,
      // and SWR will attempt to use stableHash... which involves iterating over
      // all elements in the returned array. Very slow. For more information,
      // see: https://github.com/vercel/swr/issues/1719
      //
      // Instead, check if the params to the RL calculation have changed.
      compare: (a, b) => isEqual(a?.calcParams, b?.calcParams),
      keepPreviousData,
    }
  )

  // ------------------------------------------ Return ------------------------------------------

  const staticDefaultReturn: RETURN[][] = useMemo(() => [], [])

  return {
    array: data?.array ?? staticDefaultReturn,
    isLoading: rlCalcIsLoading || baseThicknessValuesAreLoading,
  }
}

// ------------------------------------------ Helper Functions ------------------------------------------

export function useGetGenerateDerivedValuesInWebWorkerFn<RETURN = number>(
  cdfSpecification: FdeCdfSpecification,
  componentId: string | undefined,
  assetModel: GeckoAssetModel | undefined,
  baseThicknessCDF: GeckoComponentDataFrame | undefined,
  selectedInspection: GeckoInspection | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined,
  overrides: ParamOverrides = {}
): {
  baseThicknessValuesAreLoading: boolean
  calculateDataInWebWorkerFn:
    | (() => Promise<{
        calcParams: DerivedCdfWorkerCalculationParams
        array: RETURN[][]
      }>)
    | undefined
} {
  // Ensure that the baseThicknessCDF is a thickness CDF, except for EmatCombinedCategory which can use EMAT CDFs
  const isValidBaseCdf =
    isNil(baseThicknessCDF) ||
    isInspectionThicknessCDF(baseThicknessCDF) ||
    (cdfSpecification === FdeCdfSpecification.EmatCombinedCategory &&
      baseThicknessCDF?.data?.params?.source?.id === 'emat')

  if (!isValidBaseCdf) {
    throw new Error(
      `Invalid base thickness CDF used in remaining life CDF generation: ${baseThicknessCDF.id}`
    )
  }

  const generateDerivedCdfData = useGetGenerateDerivedCdfDataFn()
  const calculateDataInWebWorker = useCallback(
    () =>
      new Promise<{
        calcParams: DerivedCdfWorkerCalculationParams
        array: RETURN[][]
      }>((resolve, reject) => {
        if (isNil(baseThicknessCDF)) {
          reject("Can't calculate remaining life without a base thickness CDF")
          return
        }

        // Assert that the API calls returned valid data
        if (
          isNil(componentId) ||
          isNil(baseThicknessCDF?.tags.inspection) ||
          isNil(baseThicknessCDF?.tags.component) ||
          isNil(selectedInspection) ||
          isNil(assetModel)
        ) {
          reject("Can't calculate remaining life without all required data")
          return
        }

        generateDerivedCdfData<RETURN>({
          cdfSpecification,
          componentId,
          assetModel,
          baseCDF: baseThicknessCDF,
          selectedInspection,
          forecastComponentConfig,
          overrides,
        })
          .then(resolve)
          .catch((e) => {
            console.error(e)
            reject(e)
          })
      }),
    [
      assetModel,
      baseThicknessCDF,
      cdfSpecification,
      componentId,
      forecastComponentConfig,
      generateDerivedCdfData,
      overrides,
      selectedInspection,
    ]
  )

  return {
    baseThicknessValuesAreLoading: false,
    calculateDataInWebWorkerFn: calculateDataInWebWorker,
  }
}

// ========================================== Types ==========================================

export type ParamOverrides = {
  customCorrosionRegions?: CustomCorrosionRegion[]
  appliedRegions?: OverrideRegion[]
  thicknessValues2dArray?: number[][]
}

export type DerivedCdfWorkerCalculationParams =
  | LossWorkerProps
  | LossByPercentageWorkerProps
  | LossPerYearWorkerProps
  | TminWorkerProps
  | RcaWorkerProps
  | RemainingLifeWorkerProps
  | TimeSinceLastRepairWorkerProps
  | ThicknessByMeanWorkerProps
  | EmatCombinedCategoryWorkerProps
  | ShipDeckPITThicknessWorkerProps

// ========================================== On-Demand Generation Hooks ==========================================

// Helper to generate CDF ID
function getCdfId(
  cdfSpecification: FdeCdfSpecification,
  referenceCdf: GeckoComponentDataFrame | undefined,
  selectedCDS?: GeckoComponentDataset | undefined,
  predictedDate?: string | undefined
) {
  const thisIdPieceFromSpecs = `${cdfSpecification.source.id}${cdfSpecification.view.id}${cdfSpecification.tminMethod.id}`
  const thisIdPieceFromThicknessCDF =
    `${referenceCdf?.id ?? ''}` +
    `${referenceCdf?.data.params.aggregation.id ?? ''}` +
    `${referenceCdf?.data.params.angle_plot_setting.id ?? ''}` +
    `${referenceCdf?.data.params.algorithm.id ?? ''}` +
    `${referenceCdf?.data.binning.bin_size.width.num ?? ''}` +
    `${referenceCdf?.data.binning.bin_size.height.num ?? ''}` +
    `${selectedCDS?.id ?? ''}`
  const predictedAddition = predictedDate ? `-predicted-${predictedDate}` : ''
  return thisIdPieceFromThicknessCDF + thisIdPieceFromSpecs + predictedAddition
}

export function useHandleEmatOnDemandGeneration(
  cdfSpecification: FdeCdfSpecification,
  assetModel: GeckoAssetModel | undefined,
  componentSlug: string | undefined,
  rolodexCacheInstance: RolodexCacheInstance,
  selectedInspection?: GeckoInspection | undefined,
  componentDataFrames?: readonly GeckoComponentDataFrame[],
  selectedCDS?: GeckoComponentDataset | undefined
) {
  // ----------------------------------- Can we generate the CDF -----------------------------------

  // For EmatCombinedCategory, we need to defer the ID generation until we have the reference CDF
  // Move this inside the filledCdfArgs useMemo where we have access to referenceCdf

  // Get the "configToUse", which is the rolodex config with overrides.configOverrides applied.
  const { entries } = useGetForecastComponentConfig(selectedCDS?.id ?? null)
  const configToUse: GeckoEntryBase<ForecastComponentConfig> | undefined =
    useMemo(() => {
      return entries.at(0) as
        | GeckoEntryBase<ForecastComponentConfig>
        | undefined
    }, [entries])

  const canGenerateCdf = cdfSpecification.canCreateCdfData(
    assetModel,
    componentSlug ?? '',
    //only places fcc is used is for api crs, annular rings and 570
    configToUse?.data ?? {},
    selectedInspection
  )

  // Use the new EMAT hook to get all EMAT-specific data
  const {
    ematCategoryCdf,
    differenceFromNominalCdf,
    ematCategoryData,
    differenceFromNominalData,
    ematCategoryLoading,
    differenceFromNominalLoading,
  } = useEmatCombinedCategoryData(
    cdfSpecification,
    componentDataFrames ?? [],
    selectedCDS,
    selectedInspection
  )

  // Find a reference CDF to use as the basis for both computation and output template
  // Prefers the most appropriate CDF for each specification type
  const relevantReferenceCdf = useMemo(() => {
    return ematCategoryCdf ?? differenceFromNominalCdf
  }, [ematCategoryCdf, differenceFromNominalCdf])

  // Get the predicted date if this is a predicted inspection to make the CDF ID unique
  const predictedDate = PredictedInspectionType.includes(
    selectedInspection?.data.inspection_type as PredictedInspectionType
  )
    ? getInspectionDate({ inspection: selectedInspection })?.format(DATE_FORMAT)
    : undefined

  const thisCdfId = getCdfId(
    cdfSpecification,
    relevantReferenceCdf,
    selectedCDS,
    predictedDate
  )

  // ------------------------------------------- CDF Generation ----------------------------------------

  // - - - - - - - - - - - - - - - - - - - - - - Generate - - - - - - - - - - - - - - - - - - - - - -

  const filledCdfArgs = useMemo(
    () => {
      // For EmatCombinedCategory, we can use EMAT CDFs instead of thickness CDFs

      const shouldCreateCdf =
        relevantReferenceCdf &&
        canGenerateCdf &&
        // For EMAT combined category, ensure both required CDFs exist and data is loaded
        (cdfSpecification !== FdeCdfSpecification.EmatCombinedCategory ||
          (!ematCategoryLoading &&
            !differenceFromNominalLoading &&
            ematCategoryData &&
            differenceFromNominalData))

      const args = shouldCreateCdf
        ? {
            id: thisCdfId,
            source: cdfSpecification.source,
            aggregation:
              cdfSpecification.aggregation ??
              relevantReferenceCdf.data.params.aggregation,
            anglePlotSetting:
              relevantReferenceCdf.data.params.angle_plot_setting,
            algorithm: relevantReferenceCdf.data.params.algorithm,
            tminMethod: cdfSpecification.tminMethod,
            binning: relevantReferenceCdf.data.binning,
            geometry: relevantReferenceCdf.data.geometry,
            tags: {
              ...relevantReferenceCdf.tags,
              inspection:
                selectedInspection?.id ?? relevantReferenceCdf.tags.inspection,
            },
            view: cdfSpecification.view,
            measurementUnit: cdfSpecification.defaultMeasurementUnit,
            generation: cantorPairing(
              ematCategoryCdf?.generation ?? 1,
              differenceFromNominalCdf?.generation ?? 1
            ),
            data: async () => {
              if (ematCategoryData && differenceFromNominalData) {
                return await callEmatCombinedCategoryWorker(
                  relevantReferenceCdf,
                  selectedInspection,
                  assetModel,
                  configToUse?.data ?? {},
                  componentSlug,
                  ematCategoryData,
                  differenceFromNominalData
                )
              }
              return []
            },
          }
        : null

      return args
    },
    // FIXME: We need to disable the next line, because we memoize on id and generation for rolodex entries, not the object.
    //        Cole Spector will fix this ASAP
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      relevantReferenceCdf,
      cdfSpecification,
      canGenerateCdf,
      thisCdfId,
      ematCategoryLoading,
      differenceFromNominalLoading,
      ematCategoryData,
      differenceFromNominalData,
      assetModel?.id,
      assetModel?.generation,
      componentSlug,
      configToUse?.id,
      configToUse?.data,
      selectedInspection?.id,
      selectedInspection?.generation,
      ematCategoryCdf?.generation,
      differenceFromNominalCdf?.generation,
    ]
  )

  useCreateCustomCdf(filledCdfArgs, rolodexCacheInstance, thisCdfId)
}

type GenerateAndUploadDerivedCdfFn = (props: {
  cdfSpecification: FdeCdfSpecification
  baseCDF: GeckoComponentDataFrame
  assetModel: GeckoAssetModel
  forecastComponentConfig: ForecastComponentConfig | undefined
  baseInspection: GeckoInspection | undefined
  generationNumberOverride?: number
  // For predicted CDFs only: the real inspection used as the data source for calculations. Indicates we're generating a predicted layer.
  realInspectionForPredictionCalculation?: GeckoInspection
}) => RawCdfEntry
export function useGetGenerateAndUploadDerivedCdfFn(
  rolodexCacheInstance: RolodexCacheInstance
): GenerateAndUploadDerivedCdfFn {
  const calculateDataInWebWorker = useGetGenerateDerivedCdfDataFn()
  const { createCdfFn } = useGetCustomCdfUpsertDeleteFns(rolodexCacheInstance)

  return useCallback(
    (props) => {
      // When realInspectionForPredictionCalculation is provided, we're generating a predicted CDF
      const isPredicted = !!props.realInspectionForPredictionCalculation

      const predictedDate = isPredicted
        ? getInspectionDate({ inspection: props.baseInspection })?.format(
            DATE_FORMAT
          )
        : undefined

      const realInspectionDateForPredictionCalculation = getInspectionDate({
        inspection: props.realInspectionForPredictionCalculation,
      })?.format(DATE_FORMAT)

      const thisCdfId = getCdfId(
        props.cdfSpecification,
        props.baseCDF,
        undefined,
        predictedDate
      )
      const cdfToGenerateParams: CreateCdfParams = {
        source: props.cdfSpecification.source,
        aggregation:
          props.cdfSpecification.aggregation ??
          props.baseCDF.data.params.aggregation,
        anglePlotSetting: props.baseCDF.data.params.angle_plot_setting,
        algorithm: props.baseCDF.data.params.algorithm,
        tminMethod:
          props.cdfSpecification.tminMethod.id === GeckoTMinMethod.None.id
            ? undefined
            : props.cdfSpecification.tminMethod,
        binning: props.baseCDF.data.binning,
        geometry: props.baseCDF.data.geometry,
        tags: {
          ...props.baseCDF.tags,
          inspection: props.baseInspection?.id ?? props.baseCDF.tags.inspection,
        },
        view: props.cdfSpecification.view,
        measurementUnit: props.cdfSpecification.defaultMeasurementUnit,
        generation: props.generationNumberOverride,
        data: async () =>
          (
            await calculateDataInWebWorker({
              cdfSpecification: props.cdfSpecification,
              componentId: props.baseCDF.tags.component,
              assetModel: props.assetModel,
              baseCDF: props.baseCDF,
              selectedInspection: props.baseInspection,
              forecastComponentConfig: props.forecastComponentConfig,
              realInspectionDateForPredictionCalculation:
                realInspectionDateForPredictionCalculation,
            })
          ).array,
      }
      return createCdfFn(thisCdfId, cdfToGenerateParams)
    },
    [calculateDataInWebWorker, createCdfFn]
  )
}

// ---

/**
 * A hook that returns a function that takes in a list of CDF specifications and base values for cdf-generation,
 * generates all possible specified CDFs, and returns the ones that were successfully generated.
 * @param rolodexCacheInstance
 */
export function useGetGenerateAllValidSpecifiedCdfsFromBaseFn(
  rolodexCacheInstance: RolodexCacheInstance
) {
  const generateAndUploadDerivedCdfFn =
    useGetGenerateAndUploadDerivedCdfFn(rolodexCacheInstance)

  return useCallback(
    (props: {
      cdfSpecifications: ReadonlyArray<FdeCdfSpecification>
      baseCDF: GeckoComponentDataFrame
      assetModel: GeckoAssetModel
      forecastComponentConfig: ForecastComponentConfig
      baseInspection: GeckoInspection | undefined
    }): ReadonlyArray<FdeCdfSpecification> => {
      return props.cdfSpecifications
        .map((spec) => {
          const canGenerateCdf = spec.canCreateCdfData(
            props.assetModel,
            props.baseCDF.tags.component,
            //only places fcc is used is for api crs, annular rings and 570
            props.forecastComponentConfig,
            props.baseInspection
          )

          if (!canGenerateCdf) return null

          generateAndUploadDerivedCdfFn({
            cdfSpecification: spec,
            baseCDF: props.baseCDF,
            assetModel: props.assetModel,
            forecastComponentConfig: props.forecastComponentConfig,
            baseInspection: props.baseInspection,
          })

          return spec
        })
        .filter((s) => !isNil(s))
    },
    [generateAndUploadDerivedCdfFn]
  )
}
